import { useCallback, useRef } from 'react';

/**
 * Wraps formik's handleBlur to apply text transforms on blur.
 *
 * @param {object} formik - Formik instance (needs handleBlur, setFieldValue, values)
 * @param {object} fieldTransforms - Map of field name -> transform function
 * @returns {function} Wrapped handleBlur
 *
 * Usage:
 *   const handleBlur = useTextTransformBlur(formik, {
 *     first_name: capitalizeFirst,
 *     address_city: capitalizeWords,
 *     username: toLower,
 *   });
 */
const useTextTransformBlur = (formik, fieldTransforms) => {
  const { handleBlur, setFieldValue, values } = formik;
  const transformsRef = useRef(fieldTransforms);
  transformsRef.current = fieldTransforms;

  return useCallback(
    (e) => {
      handleBlur(e);

      const fieldName = e.target?.name || e.target?.id;
      if (!fieldName || !transformsRef.current[fieldName]) return;

      const currentValue = values[fieldName];
      const transformed = transformsRef.current[fieldName](currentValue);

      if (transformed !== currentValue) {
        setFieldValue(fieldName, transformed);
      }
    },
    [handleBlur, setFieldValue, values],
  );
};

export default useTextTransformBlur;
